/* eslint-disable consistent-return */
import { useEffect, useReducer } from "react";

import newRelicErrorReport from "web/utils/system/essentials/newRelicErrorReport";
import {
  attributesStorage,
  campaignGraphicsStorage,
  categoriesStorage,
  categoryCountsStorage,
  cmsBlocksStorage,
  customerMenuStorage,
  idsFacetsStatsStorage,
  menuStorage,
  productsDomesticTourismStorage,
  productsInListStorage,
  productsInWishlistStorage,
  productsStorage,
  productsTypesStorage,
  searchStorage,
} from "web/utils/system/storage/storagesAsync";

import { rootCategoriesIds } from "web/constants/categories";

const initialState = {
  loading: true,
  error: null,
  data: [],
  idsPrevious: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case "loading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "error":
      return {
        ...state,
        error: action.error,
        loading: false,
      };
    case "data": {
      return {
        ...state,
        data: action.data,
        loading: false,
      };
    }
    default:
      return initialState;
  }
};

const useDataCached = ({
  ids = [],
  storage,
  token,
  storeId,
  queryName,
  additionalParameters,
  skip = false,
  additionalOptions,
}) => {
  const [{ loading, error, data }, dispatch] = useReducer(
    reducer,
    initialState
  );

  useEffect(() => {
    if (skip) {
      return;
    }

    let isMounted = true;

    dispatch({ type: "loading" });
    storage
      .getItemsByKeys({
        ids,
        storeId,
        token,
        queryName,
        additionalParameters,
        additionalOptions,
      })
      .then((dataFromCache) => {
        if (isMounted) dispatch({ type: "data", data: dataFromCache });
      })
      .catch((errorData) => {
        newRelicErrorReport(
          errorData,
          "web-app/web/hooks/useDataCached/useDataCached.js - 93"
        );
        if (isMounted) dispatch({ type: "error", error: errorData });
      });

    return () => {
      isMounted = false;
    };
  }, [JSON.stringify(ids)]);

  return skip
    ? {}
    : {
        data,
        loading,
        error,
      };
};

const useDataCachedCampaignGraphics = ({ ids, token, storeId }) =>
  useDataCached({
    ids,
    token,
    storeId,
    storage: campaignGraphicsStorage,
  });

const useDataCachedProducts = ({ ids, token, storeId }) => {
  const parsedIds = ids.map((id) => parseInt(id, 10));

  return useDataCached({
    ids: parsedIds,
    token,
    storeId,
    storage: productsStorage,
    queryName: "productsByIds",
  });
};

const useDataCachedProductsInList = ({
  ids,
  token,
  storeId,
  wishlist = false,
  skip = false,
}) => {
  const parsedIds = ids.map((id) => parseInt(id, 10));

  return useDataCached({
    ids: parsedIds,
    token,
    storeId,
    skip,
    storage: wishlist ? productsInWishlistStorage : productsInListStorage,
    queryName: wishlist ? "productsInWishListByIds" : "productsInListByIds",
  });
};

const useDataCachedProductsDomesticTourism = ({
  ids,
  token,
  storeId,
  additionalParameters,
  additionalOptions,
}) =>
  useDataCached({
    ids,
    token,
    storeId,
    storage: productsDomesticTourismStorage,
    additionalParameters,
    additionalOptions,
  });

const useDataCachedCategories = ({ ids, token, storeId }) =>
  useDataCached({
    ids: ids
      .map((id) => (rootCategoriesIds.includes(id) ? null : +id))
      .filter(Boolean),
    token,
    storeId,
    storage: categoriesStorage,
  });

const useDataCachedCategoryCounts = ({ ids, token, storeId }) =>
  useDataCached({
    ids,
    token,
    storeId,
    storage: categoryCountsStorage,
    queryName: "categoryCounts",
  });

const useDataCachedAttributes = ({ ids, token, storeId, skip = false }) =>
  useDataCached({
    ids,
    token,
    storeId,
    storage: attributesStorage,
    skip,
  });

const useDataCachedIdsFacetsStats = ({ options }) =>
  useDataCached({
    ids: [options],
    storage: idsFacetsStatsStorage,
    queryName: "productsIdsFacetsStats",
  });

const useDataCachedSearch = ({ options }) =>
  useDataCached({
    ids: [options],
    storage: searchStorage,
    queryName: "productsSearch",
  });

const useDataCachedProductsTypes = ({ ids, token, storeId, skip = false }) => {
  const parsedIds = ids.map((id) => parseInt(id, 10));

  return useDataCached({
    ids: parsedIds,
    token,
    storeId,
    storage: productsTypesStorage,
    skip,
  });
};

const useDataCachedMenu = ({ token, storeId }) =>
  useDataCached({
    ids: ["menu"],
    token,
    storeId,
    storage: menuStorage,
    queryName: "menu",
  });

const useDataCachedCustomerMenu = ({ token, storeId }) => {
  return useDataCached({
    ids: ["customerMenu"],
    token,
    storeId,
    storage: customerMenuStorage,
    queryName: "customerMenu",
  });
};

const useDataCachedCmsBlocks = ({ ids, queryName }) =>
  useDataCached({
    ids,
    storage: cmsBlocksStorage,
    queryName,
  });

export {
  useDataCachedProducts,
  useDataCachedProductsInList,
  useDataCachedProductsDomesticTourism,
  useDataCachedCategories,
  useDataCachedCategoryCounts,
  useDataCachedCampaignGraphics,
  useDataCachedAttributes,
  useDataCachedIdsFacetsStats,
  useDataCachedSearch,
  useDataCachedProductsTypes,
  useDataCachedMenu,
  useDataCachedCustomerMenu,
  useDataCachedCmsBlocks,
};
